import React, { useEffect, useRef, useState } from "react";
import "./PartnerSection.css";
import { useTranslation } from "react-i18next";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../../config/firebase";

import { DEFAULT_PARTNERS } from "../../../config/defaultPartners";

function PartnersSection() {
  const { t, i18n } = useTranslation();
  const scrollerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  // null = chargement en cours, [] = aucun partenaire en base
  const [dbPartners, setDbPartners] = useState(null);

  const lang =
    i18n.language && i18n.language.startsWith("en") ? "en" : "fr";

  // Écoute de la collection `partners` en temps réel (gérée depuis l'admin)
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "partners"),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setDbPartners(data);
      },
      (error) => {
        console.error("Erreur de chargement des partenaires :", error);
        setDbPartners([]);
      },
    );

    return () => unsubscribe();
  }, []);

  // Tant que la base est vide, on affiche les partenaires historiques
  const source =
    dbPartners && dbPartners.length > 0 ? dbPartners : DEFAULT_PARTNERS;

  const partners = source.map((p, index) => ({
    id: p.id || index,
    img: p.logo,
    title: p.name,
    desc: lang === "en" ? p.desc_en || p.desc_fr : p.desc_fr,
    url: p.url,
  }));
  const isReady = dbPartners !== null;

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    let animationId;
    const speed = 0.8;

    const step = () => {
      if (!isPaused) {
        scroller.scrollLeft += speed;
        if (scroller.scrollLeft >= scroller.scrollWidth / 2) {
          scroller.scrollLeft = 0;
        }
      }
      animationId = requestAnimationFrame(step);
    };

    animationId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationId);
  }, [isPaused, isReady]);

  // On attend Firestore pour éviter un « flash » de l'ancienne liste
  if (!isReady) return null;

  return (
    <section className="partners-section">
      <p className="subtitle">
        {t("partners_section.title")}{" "}
        <span className="gradient-text">{t("partners_section.subtitle")}</span>
      </p>

      {/* --- AFFICHAGE PC (GRILLE) --- */}
      <div className="partners-grid desktop-only">
        {partners.map((partner) => (
          <PartnerCard key={partner.id} {...partner} />
        ))}
      </div>

      {/* --- AFFICHAGE MOBILE (CARROUSEL INFINI) --- */}
      <div
        className="partners-carousel-wrapper mobile-only"
        ref={scrollerRef}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
      >
        <div className="partners-track">
          {/* Liste 1 */}
          {partners.map((partner) => (
            <div key={`a-${partner.id}`} className="carousel-item">
              <PartnerCard {...partner} />
            </div>
          ))}

          {/* Liste 2 (Duplicata) */}
          {partners.map((partner) => (
            <div key={`b-${partner.id}`} className="carousel-item">
              <PartnerCard {...partner} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Composant Helper
function PartnerCard({ img, title, desc, url }) {
  const card = (
    <div className="partner-card">
      <div className="logo-container">
        <img src={img} alt={title} />
      </div>
      <h4>{title}</h4>
      <p>{desc}</p>
    </div>
  );

  // Lien optionnel vers le site du partenaire
  if (!url) return card;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="partner-card-link"
    >
      {card}
    </a>
  );
}

export default PartnersSection;
